interface FuseSpec {
  current: number;
  type: string;
}

export const fuseTypes = ['NH000', 'NH00', 'NH1', 'NH2', 'NH3'] as const;

export const nhFuseCatalog: Record<string, number[]> = {
  NH000: [6, 10, 16, 20, 25, 32, 35, 40, 50, 63, 80, 100],
  NH00: [6, 10, 16, 20, 25, 32, 35, 40, 50, 63, 80, 100, 125, 160],
  NH1: [50, 63, 80, 100, 125, 160, 200, 224, 250],
  NH2: [125, 160, 200, 224, 250, 315, 355, 400],
  NH3: [315, 355, 400, 425, 500, 630],
};

export const getRatingsForFuseType = (type: string): number[] => {
  return nhFuseCatalog[type] ?? [];
};

// Default fuse specifications based on cable cross section
// Current values must exist in nhFuseCatalog for the given type
const defaultFuseSpecs: Record<number, FuseSpec> = {
  16: { current: 63, type: 'NH000' },
  25: { current: 80, type: 'NH00' },
  35: { current: 100, type: 'NH00' },
  50: { current: 125, type: 'NH00' },
  70: { current: 160, type: 'NH00' },
  95: { current: 200, type: 'NH1' },
  120: { current: 250, type: 'NH1' },
  150: { current: 315, type: 'NH2' },
  185: { current: 355, type: 'NH2' },
  240: { current: 400, type: 'NH2' },
};

// Typical fuse ranges for network elements
// Current values must exist in nhFuseCatalog for the given type
const networkElementFuseRanges = {
  kabelverteiler: {
    min: 100,
    max: 400,
    typical: {
      small: { current: 160, type: 'NH00' },    // Typical for smaller distributors
      medium: { current: 250, type: 'NH1' },    // Medium load
      large: { current: 400, type: 'NH2' }      // Heavy load
    }
  },
  muffe: {
    min: 100,
    max: 400,
    typical: {
      small: { current: 160, type: 'NH00' },    // Typical for smaller joints
      medium: { current: 250, type: 'NH1' },    // Medium load
      large: { current: 400, type: 'NH2' }      // Heavy load
    }
  },
  trafostation: {
    min: 400,
    max: 630,
    typical: {
      small: { current: 400, type: 'NH2' },     // Smaller transformer stations
      medium: { current: 500, type: 'NH3' },    // Medium sized stations
      large: { current: 630, type: 'NH3' }      // Large stations/high power demand
    }
  }
};

export const getDefaultFuse = (crossSection: number, elementType?: 'kabelverteiler' | 'trafostation' | 'muffe'): FuseSpec => {
  if (elementType) {
    const specs = networkElementFuseRanges[elementType];
    
    // For Kabelverteiler
    if (elementType === 'kabelverteiler') {
      if (crossSection <= 95) return specs.typical.small;
      if (crossSection <= 150) return specs.typical.medium;
      return specs.typical.large;
    }
    
    // For Muffe
    if (elementType === 'muffe') {
      if (crossSection <= 95) return specs.typical.small;
      if (crossSection <= 150) return specs.typical.medium;
      return specs.typical.large;
    }
    
    // For Trafostation
    if (elementType === 'trafostation') {
      if (crossSection <= 150) return specs.typical.small;
      if (crossSection <= 240) return specs.typical.medium;
      return specs.typical.large;
    }
  }

  // Fallback to cross-section based selection if no element type specified
  const validCrossSections = Object.keys(defaultFuseSpecs)
    .map(Number)
    .sort((a, b) => a - b);
  
  const matchingCrossSection = validCrossSections.reduce((prev, curr) => {
    if (curr <= crossSection && curr > prev) {
      return curr;
    }
    return prev;
  }, 0);

  return defaultFuseSpecs[matchingCrossSection] || defaultFuseSpecs[16]; // Default to smallest if no match
};
